import {
  Component,
  Inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { DecimalPipe } from '@angular/common';
import {
  FileWithPath,
  FolderItem,
  UploadDialogData,
} from '../../model/upload-file-folder-interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CONSTANTS_DETAILS } from '../../constant/constants';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { AppService } from '../../../services/app-service';

@Component({
  selector: 'app-upload-file-folder-pop-up',
  imports: [
    MatDialogModule,
    MatDividerModule,
    MatButtonModule,
    MatIconButton,
    DecimalPipe,
    TranslateModule,
  ],
  templateUrl: './upload-file-folder-pop-up.component.html',
  styleUrl: './upload-file-folder-pop-up.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadFileFolderPopUpComponent implements OnInit {
  constructor(
    public _dialogRef: MatDialogRef<UploadFileFolderPopUpComponent>,
    @Inject(MAT_DIALOG_DATA)
    public _data: UploadDialogData & { existingNames?: Set<string> },
    private _snackbarService: SnackbarService,
    private _translateService: TranslateService,
    private _appService: AppService,
  ) {}

  public selectedFiles = signal<FileWithPath[]>([]);
  public displayItems = signal<FolderItem[]>([]);
  public uploading = signal<boolean>(false);
  public defaultChunkSize = signal<number>(1);
  public defaultThreadCount = signal<number>(1);
  public enableHashValue = signal<boolean>(false);

  /**
   * Angular lifecycle hook that initializes the component after constructor
   *
   * @memberof UploadFileFolderPopUpComponent
   */
  public ngOnInit(): void {
    this._appService.getChunkConfig().then((config) => {
      if (config.chunkSizeInMB) {
        this.defaultChunkSize.set(config.chunkSizeInMB);
      }
      if (config.maxConcurrentRequests) {
        this.defaultThreadCount.set(config.maxConcurrentRequests);
      }
      if (config.isHashEnabled !== undefined) {
        this.enableHashValue.set(config.isHashEnabled);
      }
      this._appService.initHasher();
    });
  }

  /**
   * Checks if a file type is restricted based on its extension.
   *
   * @param {string} fileName
   * @return {boolean}
   * @memberof UploadFileFolderPopUpComponent
   */
  private isFileTypeRestricted(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension) return false;

    return CONSTANTS_DETAILS.RESTRICTED_FILE_TYPES.includes(extension);
  }

  /**
   * Filters out restricted file types from an array of files.
   *
   * @param {FileWithPath[]} files
   * @return {{ allowedFiles: FileWithPath[], restrictedCount: number }}
   * @memberof UploadFileFolderPopUpComponent
   */
  private filterRestrictedFiles(files: FileWithPath[]): {
    allowedFiles: FileWithPath[];
    restrictedCount: number;
  } {
    const allowedFiles: FileWithPath[] = [];
    const restrictedFiles: string[] = [];

    files.forEach((file) => {
      const isRestricted = this.isFileTypeRestricted(file.name);
      if (isRestricted) {
        restrictedFiles.push(file.name);
      } else {
        allowedFiles.push(file);
      }
    });

    // Show notification if any files were restricted
    if (restrictedFiles.length > 0) {
      let message: string;
      if (restrictedFiles.length === 1) {
        const template = this._translateService.instant(
          'restrictedFileExcludedSingleCount',
        ) as string;
        message = template.replace(
          '{count}',
          restrictedFiles.length.toString(),
        );
      } else {
        const template = this._translateService.instant(
          'restrictedFileExcludedMultiple',
        ) as string;
        message = template.replace(
          '{count}',
          restrictedFiles.length.toString(),
        );
      }

      this._snackbarService.open({
        message,
        type: 'error',
      });
    }

    return { allowedFiles, restrictedCount: restrictedFiles.length };
  }

  /**
   * Handles the file selection process.
   *
   * @param {Event} event
   * @return {*}
   * @memberof UploadFileFolderPopUpComponent
   */
  public filesPicked(event: Event) {
    const input = event.target as HTMLInputElement;
    const list = input.files;
    if (!list) return;

    const picked = Array.from(list);
    const current = this.selectedFiles();
    const valid: FileWithPath[] = [];
    const restrictedFiles: string[] = [];

    for (const file of picked) {
      // Check if file type is restricted
      if (this.isFileTypeRestricted(file.name)) {
        restrictedFiles.push(file.name);
        continue;
      }

      // Check for duplicates based on name and skip individual files, don't return early
      if (current.some((f) => f.name === file.name)) {
        continue;
      }

      // Check against existing names and skip individual files, don't return early
      if (this._data?.existingNames?.has(file.name)) {
        continue;
      }

      const fileWithPath = Object.assign(file, {
        relativePath: file.webkitRelativePath || file.name,
      });
      valid.push(fileWithPath);
    }

    // Show notification if any files were restricted
    if (restrictedFiles.length > 0) {
      let message: string;
      if (restrictedFiles.length === 1) {
        const template = this._translateService.instant(
          'restrictedFileSingleCount',
        ) as string;
        message = template.replace(
          '{count}',
          restrictedFiles.length.toString(),
        );
      } else {
        const template = this._translateService.instant(
          'restrictedFileMultiple',
        ) as string;
        message = template.replace(
          '{count}',
          restrictedFiles.length.toString(),
        );
      }

      this._snackbarService.open({
        message,
        type: 'error',
      });
    }

    // Only add files if we have valid ones
    if (valid.length > 0) {
      const allFiles = [...current, ...valid];
      this.selectedFiles.set(allFiles);
      this.updateDisplayItems(allFiles);
    }
    input.value = '';
  }
  /**
   * Handles the file upload process.
   *
   * @return {*}
   * @memberof AddAttachmentsDialogComponent
   */
  public async uploadFiles() {
    if (this.selectedFiles().length === 0) return;

    this.uploading.set(true);
    this._dialogRef.close(true);
    // Perform the upload in background
    this._appService.uploadDataInChunksNoMergeForAll(
      this.selectedFiles(),
      this.defaultChunkSize(),
      this.defaultThreadCount(),
      this.enableHashValue(),
    );
  }

  public close() {
    this._dialogRef.close(false);
  }
  /**
   * Handles the drag over event.
   *
   * @param {DragEvent} event
   * @memberof AddAttachmentsDialogComponent
   */
  public dragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }
  /**
   * Handles the drag enter event.
   *
   * @param {DragEvent} event
   * @memberof UploadFileFolderPopUpComponent
   */
  public dragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }
  /**
   * Handles the drag leave event.
   *
   * @param {DragEvent} event
   * @memberof UploadFileFolderPopUpComponent
   */
  public dragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }
  /**
   * Handles the drop over event.
   *
   * @param {DragEvent} event
   * @memberof AddAttachmentsDialogComponent
   */
  public async dropOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!event.dataTransfer?.items) return;

    const newFiles: FileWithPath[] = [];
    const items = Array.from(event.dataTransfer.items);

    // Process all items concurrently and wait for all to complete
    const fileCollectionPromises = items.map(async (dataItem) => {
      const item = dataItem.webkitGetAsEntry?.();
      if (item) {
        try {
          const collected = await this.traverseFileTree(item);
          return collected;
        } catch {
          return [];
        }
      }
      return [];
    });

    // Wait for all file collection operations to complete
    const allCollectedFiles = await Promise.all(fileCollectionPromises);

    // Flatten the results
    for (const collected of allCollectedFiles) {
      newFiles.push(...collected);
    }

    // Filter out restricted file types
    const { allowedFiles: allowedNewFiles } =
      this.filterRestrictedFiles(newFiles);

    // Filter out duplicates based on file name and relative path
    const currentFiles = this.selectedFiles();
    const filteredNewFiles = allowedNewFiles.filter((newFile) => {
      const newFileId = `${newFile.name}-${newFile.relativePath || newFile.name}`;
      return !currentFiles.some((existingFile) => {
        const existingFileId = `${existingFile.name}-${existingFile.relativePath || existingFile.name}`;
        return existingFileId === newFileId;
      });
    });

    // Filter out files that already exist in the target location
    const finalFilteredFiles = filteredNewFiles.filter((file) => {
      return !this._data?.existingNames?.has(file.name);
    });

    // Only update if we have new files to add
    if (finalFilteredFiles.length > 0) {
      const allFiles = [...currentFiles, ...finalFilteredFiles];
      this.selectedFiles.set(allFiles);
      this.updateDisplayItems(allFiles);
    }
  }

  /**
   * Recursively traverses a file tree to collect all files with their relative paths.
   *
   * @param {*} item
   * @param {string} [path='']
   * @return {*}  {Promise<FileWithPath[]>}
   * @memberof UploadFileFolderPopUpComponent
   */
  private traverseFileTree(
    item: FileSystemEntry,
    path = '',
  ): Promise<FileWithPath[]> {
    return new Promise((resolve) => {
      if (item.isFile) {
        const fileEntry = item as FileSystemFileEntry;
        fileEntry.file(
          (file: File) => {
            try {
              // Attach relative path info
              const fileWithPath: FileWithPath = Object.assign(file, {
                relativePath: path ? `${path}/${file.name}` : file.name,
              });
              resolve([fileWithPath]);
            } catch {
              resolve([]);
            }
          },
          () => {
            resolve([]);
          },
        );
      } else if (item.isDirectory) {
        const dirEntry = item as FileSystemDirectoryEntry;
        const dirReader = dirEntry.createReader();
        const entries: FileSystemEntry[] = [];

        // Recursive reader to handle large directories
        const readEntries = () => {
          dirReader.readEntries(
            async (results: FileSystemEntry[]) => {
              if (results.length) {
                entries.push(...results);
                readEntries();
              } else {
                try {
                  // Process sub-items after reading all
                  const promises = entries.map((entry) =>
                    this.traverseFileTree(
                      entry,
                      path ? `${path}/${dirEntry.name}` : dirEntry.name,
                    ),
                  );
                  const all = await Promise.all(promises);
                  resolve(all.flat());
                } catch {
                  resolve([]);
                }
              }
            },
            () => {
              resolve([]);
            },
          );
        };

        readEntries();
      } else {
        resolve([]);
      }
    });
  }

  /**
   * Updates the display items based on selected files
   * Groups files by folders or shows individual files
   * Only includes uploadable (non-restricted) files in counts and displays
   */
  private updateDisplayItems(files: FileWithPath[]) {
    const displayItems: FolderItem[] = [];
    const folderMap = new Map<string, FileWithPath[]>();
    const individualFiles: FileWithPath[] = [];

    // Group files by their folder structure
    for (const file of files) {
      const relativePath = file.relativePath || file.name;
      const pathParts = relativePath.split('/');

      if (pathParts.length > 1) {
        // File is inside a folder
        const folderName = pathParts[0];
        if (!folderMap.has(folderName)) {
          folderMap.set(folderName, []);
        }
        folderMap.get(folderName)?.push(file);
      } else {
        // Individual file
        individualFiles.push(file);
      }
    }

    // Create folder items with only uploadable files
    for (const [folderName, folderFiles] of folderMap) {
      const uploadableFiles = folderFiles; // Files are already filtered before this method
      const totalSize = uploadableFiles.reduce(
        (acc, file) => acc + file.size,
        0,
      );

      // Only add folder if it has uploadable files
      if (uploadableFiles.length > 0) {
        displayItems.push({
          name: folderName,
          size: totalSize,
          files: uploadableFiles,
          isFolder: true,
        });
      }
    }

    // Create individual file items (already filtered)
    for (const file of individualFiles) {
      displayItems.push({
        name: file.name,
        size: file.size,
        files: [file],
        isFolder: false,
      });
    }

    this.displayItems.set(displayItems);
  }

  /**
   * Removes a file or folder from the selected files list.
   *
   * @param {FolderItem} item
   * @memberof AddAttachmentsDialogComponent
   */
  public removeItem(item: FolderItem) {
    const currentFiles = this.selectedFiles();
    const remainingFiles = currentFiles.filter(
      (file) => !item.files.some((itemFile) => itemFile === file),
    );
    this.selectedFiles.set(remainingFiles);
    this.updateDisplayItems(remainingFiles);
  }

  /**
   * Removes a file from the selected files list.
   *
   * @param {File} file
   * @memberof AddAttachmentsDialogComponent
   */
  public removeFile(file: FileWithPath) {
    const remainingFiles = this.selectedFiles().filter((f) => f !== file);
    this.selectedFiles.set(remainingFiles);
    this.updateDisplayItems(remainingFiles);
  }
}
